import api from './api';
import { ICheckoutPrice } from '../interface/checkoutPrice.interface';
import { BillingInterval } from '../components/billing/types';
import type { SubscriptionLockStatusResponse } from '../utility/subscriptionLock';

const DAY_IN_MS = 1000 * 60 * 60 * 24;

export const calculateTrialDisplayDaysRemaining = (
  trialEndDate: string | Date | null | undefined,
): number | null => {
  if (!trialEndDate) return null;

  const trialEndsAt = new Date(trialEndDate);
  if (Number.isNaN(trialEndsAt.getTime())) return null;

  return Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / DAY_IN_MS));
};

// API Response wrapper type (backend ResponseHandler format)
interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

// Helper to extract data from ResponseHandler format
const extractData = <T,>(response: any): T => {
  // Handle nested data structure from ResponseHandler
  if (response.data?.data !== undefined) {
    return response.data.data;
  }
  // Fallback for direct data
  return response.data;
};

// Helper to extract data and unwrap single-key objects (e.g., {plans: [...]} -> [...])
const extractAndUnwrap = <T,>(response: any, key?: string): T => {
  const data = extractData<Record<string, unknown>>(response);

  // If a specific key is provided, extract it
  if (key && data && typeof data === 'object' && key in data) {
    return data[key] as T;
  }

  // Auto-unwrap if data is an object with a single key
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const keys = Object.keys(data);
    if (keys.length === 1 && key === undefined) {
      return data[keys[0]] as T;
    }
  }

  return data as T;
};

// Interfaces
export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  price_per_student_month: string; // Backend returns as string
  description: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  plan_features?: PlanFeature[];
}

export interface PlanFeature {
  plan_id: string;
  feature_code: string;
  value: string;
  created_at: string;
  updated_at: string;
  feature?: {
    feature_code: string;
    description: string;
    category: string;
    created_at: string;
  };
}

/**
 * Minimal feature flag as returned by `/me/subscription` — plan_id and the
 * audit columns that `PlanFeature` carries are not part of that payload.
 */
export interface OrgFeatureFlag {
  feature_code: string;
  value: string;
}

/** Raw `/me/subscription` payload — features arrive grouped by category. */
export interface MySubscriptionWithFeaturesResponse {
  subscription: OrganizationSubscription;
  features: Record<string, { code: string; name?: string; value: string }[]>;
  featureCount: number;
}

export interface OrganizationSubscription {
  currency?: string;
  id: string;
  organization_id: string;
  plan_id: string;
  status: string; // BE returns UPPERCASE from Prisma ('TRIAL', 'ACTIVE', 'EXPIRED', 'CANCELLED')
  trial_start_date?: string;
  trial_end_date?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  is_trial: boolean;
  auto_renew: boolean;
  cancelled_at?: string;
  scheduled_plan_id?: string | null;
  trialEndDate?: string;
  plan: SubscriptionPlan;
  features?: PlanFeature[];
  student_count?: number;
  studentCount?: number;
  /** Active institute-level discount campaign, if any. `null`/absent for the vast
   *  majority of organizations — every consumer must treat both as "no discount". */
  activeDiscount?: ActiveDiscount | null;
  /** Billing cycle of the paid subscription, as `/me/subscription` returns it: 'MONTHLY' | 'ANNUAL'. */
  billingInterval?: string;
}

/** Active institute-level discount summary, nested at `subscription.activeDiscount`. */
export interface ActiveDiscount {
  campaignName: string;
  discountType: 'PERCENTAGE' | 'FLAT_AMOUNT' | 'PER_STUDENT';
  discountValue: number;
  /** ISO string, set for DATE_RANGE discounts. */
  endDate: string | null;
  /** Set for CYCLE_COUNT discounts. */
  remainingCycles: number | null;
}

export interface PaymentHistory {
  id: string;
  subscription_id: string;
  organization_id: string;
  amount: number;
  currency: string;
  payment_gateway: string;
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';

  // PayHere specific
  payhere_order_id?: string | null;
  payhere_payment_id?: string | null;
  payhere_status_code?: string | null;
  payhere_md5sig?: string | null;
  payhere_method?: string | null;
  payhere_card_holder?: string | null;
  payhere_card_no?: string | null;

  // Stripe specific
  stripe_session_id?: string | null;
  stripe_payment_intent?: string | null;
  stripe_customer_id?: string | null;

  // WebXPay specific
  webxpay_order_id?: string | null;
  webxpay_session_id?: string | null;
  webxpay_receipt?: string | null;

  payment_date?: string | null;
  created_at: string;
  updated_at?: string;
  metadata?: Record<string, any> | null;

  // Enriched relations when available
  subscription?: {
    id?: string;
    plan_id?: string;
    student_count?: number;
    plan?: SubscriptionPlan;
  };
  organization?: {
    id: string;
    name?: string | null;
    email?: string | null;
  };
}

export interface InvoicePayload {
  invoice_number: string;
  payment_id: string;
  subscription_id: string;
  organization: {
    id: string;
    name?: string | null;
    email?: string | null;
  };
  plan: {
    id?: string;
    name: string;
  };
  billing_cycle?: string;
  student_count?: number;
  amount: number;
  currency: string;
  payment_gateway: string;
  payment_status: string;
  payment_date?: string | null;
  created_at: string;
  metadata?: Record<string, any> | null;
}

export type BillingInvoiceStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';

export interface BillingInvoice {
  id: string;
  subscription_id: string;
  organization_id: string;
  invoice_number: string;
  billing_cycle_start: string;
  billing_cycle_end: string;
  due_date: string;
  plan_id: string;
  plan_name: string;
  amount: string | number;
  currency: string;
  student_count?: number | null;
  status: BillingInvoiceStatus;
  paid_date?: string | null;
  paid_amount?: string | number | null;
  is_restricted?: boolean;
  restriction_date?: string | null;
  payment_history_id?: string | null;
  payment_gateway?: string | null;
  billing_interval?: BillingInterval;
  notes?: string | null;
  metadata?: Record<string, any> | null;
  created_at: string;
  updated_at?: string;
  /** Pre-discount amount for this invoice. Already flowed through the API unconditionally —
   *  absent/equal to `amount` means no discount was applied. */
  gross_amount?: number | string;
  /** Amount deducted by any applied discount(s). Absent or 0 means no discount. */
  discount_amount?: number | string;
  /** Per-campaign discount breakdown, present only when `discount_amount > 0`. */
  discountApplications?: {
    grossAmount: number;
    discountAmount: number;
    netAmount: number;
    campaignName: string;
    discountType: string;
    discountValue: number;
  }[];
  subscription?: {
    id?: string;
    plan_id?: string;
    student_count?: number;
    plan?: SubscriptionPlan;
  };
  payment?: PaymentHistory;
  organization?: {
    id: string;
    name?: string | null;
    email?: string | null;
    address?: string | null;
  };
}

export interface InitiatePaymentRequest {
  subscription_id: string;
  plan_id: string;
  gateway: 'payhere' | 'webxpay';
  amount: number;
  currency: string;
  billing_interval?: BillingInterval;
  billing_invoice_id?: string;
}

export interface InitiatePaymentResponse {
  redirectUrl?: string;
  redirect_url?: string;
  paymentId?: string;
  payment_id?: string;
  orderId?: string;
  order_id?: string;
  sessionId?: string;
  session_id?: string;
  metadata?: Record<string, any>;
  gateway?: string;
}

// Billing Summary interfaces
export interface IBillingSummary {
  currentPlan: {
    id: string;
    name: string;
    price: number;
    studentCount: number;
  };
  outstandingBalance: number;
  currency: string;
  unpaidInvoiceCount: number;
  nextInvoice: {
    id: string;
    invoiceNumber: string;
    amount: number;
    dueDate: string;
    daysUntilDue: number;
  } | null;
  mostOverdueInvoice: {
    id: string;
    invoiceNumber: string;
    amount: number;
    dueDate: string;
    daysOverdue: number;
  } | null;
  isTrial: boolean;
  trialEndsAt: string | null;
  subscriptionStatus: string;
  nextPaymentDate: string | null;
  autoRenew: boolean;
}

export interface GracePeriodStatus {
  isInGracePeriod: boolean;
  daysRemaining?: number;
  gracePeriodEndDate?: Date;
  unpaidInvoiceCount: number;
  outstandingAmount: number;
  severity: 'info' | 'warning' | 'critical';
  isRestricted: boolean;
  unpaidInvoices?: {
    id: string;
    invoiceNumber: string;
    amount: number;
    dueDate: Date;
    daysOverdue: number;
  }[];
  lockoutDeferredUntil?: Date;
  lockoutDeferredReason?: string;
  isTrial: boolean;
  trialDaysRemaining: number | null;
  /** True when SUBSCRIPTION_TEST_MODE=true on the server (crons run per-minute, timings compressed) */
  isTestMode?: boolean;
  /** Active timing values — only present when isTestMode is true */
  timings?: {
    trialMinutes: number;
    billingCycleMinutes: number;
    invoiceDueMinutes: number;
    overdueGraceMinutes: number;
  };
}

export interface TrialStatus {
  isTrial: boolean;
  trialDaysRemaining: number | null;
  trialEndsAt: string | null;
  severity: 'info' | 'warning' | 'critical' | null;
}

// API Service Functions

const getPlanCurrencyParams = () => {
  const currency = localStorage.getItem('subscriptionCurrency') || localStorage.getItem('currency');
  return currency ? { currency } : {};
};

/**
 * Fetch all available subscription plans
 */
export const fetchSubscriptionPlans = async (): Promise<{ data: SubscriptionPlan[] }> => {
  const response = await api.get<ApiResponse<{ plans: SubscriptionPlan[] }>>(
    '/subscription/plans',
    {
      params: getPlanCurrencyParams(),
    },
  );
  return { data: extractAndUnwrap<SubscriptionPlan[]>(response, 'plans') };
};

/**
 * Fetch a specific plan by ID or slug
 */
export const fetchPlanDetails = async (
  idOrSlug: string | number,
): Promise<{ data: SubscriptionPlan }> => {
  const response = await api.get<ApiResponse<SubscriptionPlan>>(`/subscription/plans/${idOrSlug}`, {
    params: getPlanCurrencyParams(),
  });
  return { data: extractData<SubscriptionPlan>(response) };
};

/**
 * Get current organization's subscription details
 */
/**
 * Price of a plan at checkout for the caller's school — the amount the card will
 * be charged (server-computed; the browser no longer works it out).
 */
export const fetchMyCheckoutPrice = async (
  planId: string,
  billingInterval: string,
): Promise<ICheckoutPrice> => {
  const response = await api.get<ApiResponse<ICheckoutPrice>>('/me/subscription/checkout-price', {
    params: { plan_id: planId, billing_interval: billingInterval },
  });
  return extractData<ICheckoutPrice>(response);
};

export const fetchCurrentSubscription = async (): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.get<ApiResponse<{ subscription: OrganizationSubscription }>>(
    '/me/subscription',
  );
  const subscription = extractAndUnwrap<OrganizationSubscription>(response, 'subscription');
  if (subscription?.currency) localStorage.setItem('subscriptionCurrency', subscription.currency);
  return { data: subscription };
};

/**
 * Current subscription PLUS the organization's plan features, read from the
 * same `/me/subscription` call.
 *
 * `/me/features` requires the BILLING_MANAGEMENT privilege, so Teachers and
 * Parents can never load it — which left every feature lock silently open for
 * them. `/me/subscription` is JWT-only and already returns the identical
 * feature set (grouped by category), so it is the one source every role can
 * read. Features are flattened here to the flat `{ feature_code, value }`
 * shape the subscription context matches on.
 *
 * `subscription` is null when the org has no subscription row yet (ETL setup
 * incomplete) — the backend returns `data: null` in that case.
 */
export const fetchMySubscriptionWithFeatures = async (): Promise<{
  data: { subscription: OrganizationSubscription | null; features: OrgFeatureFlag[] };
}> => {
  const response = await api.get<ApiResponse<MySubscriptionWithFeaturesResponse | null>>(
    '/me/subscription',
  );
  const data = extractData<MySubscriptionWithFeaturesResponse | null>(response);

  const features: OrgFeatureFlag[] = Object.values(data?.features || {})
    .flat()
    .map((feature) => ({ feature_code: feature.code, value: feature.value }));

  if (data?.subscription?.currency) {
    localStorage.setItem('subscriptionCurrency', data.subscription.currency);
  }
  return { data: { subscription: data?.subscription ?? null, features } };
};

/**
 * Get organization subscription by organization ID
 */
export const fetchOrganizationSubscription = async (
  organizationId: number | string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.get<ApiResponse<{ subscription: OrganizationSubscription }>>(
    `/subscription/organization/${organizationId}`,
  );
  const data = extractData<{ subscription: OrganizationSubscription }>(response);
  return { data: data.subscription };
};

/**
 * Create a new subscription (starts trial)
 */
export const createSubscription = async (data: {
  organization_id: string;
  plan_id: string;
  student_count?: number;
  is_trial?: boolean;
  trial_days?: number;
}): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    '/subscription/organization',
    data,
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Update existing subscription
 */
export const updateSubscription = async (
  organizationId: string,
  data: {
    plan_id?: string;
    status?: string;
    auto_renew?: boolean;
  },
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.put<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}`,
    data,
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (
  organizationId: number | string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.delete<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}`,
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Schedule subscription cancellation at end of billing period
 */
export const scheduleCancellation = async (
  organizationId: number | string,
  reason?: string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}/schedule-cancellation`,
    { reason },
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Upgrade subscription plan (immediate effect with pro-rata billing)
 */
export const upgradePlan = async (
  organizationId: string,
  newPlanId: string,
  reason?: string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}/upgrade`,
    { newPlanId, reason },
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Schedule downgrade to a lower plan (takes effect at end of billing cycle)
 */
export const scheduleDowngrade = async (
  organizationId: string,
  newPlanId: string,
  reason?: string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}/downgrade`,
    { newPlanId, reason },
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Cancel a scheduled downgrade (user keeps current plan)
 */
export const cancelScheduledDowngrade = async (
  organizationId: number | string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}/cancel-scheduled-downgrade`,
    {},
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Get all features for organization's subscription
 */
export const fetchSubscriptionFeatures = async (
  organizationId: number | string,
): Promise<{
  data: { features: PlanFeature[]; featuresByCategory: Record<string, PlanFeature[]> };
}> => {
  const response = await api.get<
    ApiResponse<{ features: PlanFeature[]; featuresByCategory: Record<string, PlanFeature[]> }>
  >(`/subscription/organization/${organizationId}/features`);
  return {
    data: extractData<{
      features: PlanFeature[];
      featuresByCategory: Record<string, PlanFeature[]>;
    }>(response),
  };
};

/**
 * Check if a specific feature is available
 */
export const checkFeature = async (
  organizationId: number | string,
  featureCode: string,
): Promise<{ data: { hasFeature: boolean; feature?: PlanFeature } }> => {
  const response = await api.get<ApiResponse<{ hasFeature: boolean; feature?: PlanFeature }>>(
    `/subscription/organization/${organizationId}/features/${featureCode}`,
  );
  return { data: extractData<{ hasFeature: boolean; feature?: PlanFeature }>(response) };
};

/**
 * Initiate payment for subscription upgrade
 */
export const initiatePayment = async (
  data: InitiatePaymentRequest,
): Promise<{ data: InitiatePaymentResponse }> => {
  const response = await api.post<ApiResponse<InitiatePaymentResponse>>(
    '/subscription/payment/initiate',
    data,
  );
  return { data: extractData<InitiatePaymentResponse>(response) };
};

/**
 * Get billing history for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const fetchMyBillingHistory = async (): Promise<{ data: BillingInvoice[] }> => {
  const response = await api.get<ApiResponse<BillingInvoice[]>>('/me/billing');
  const data = extractData<BillingInvoice[]>(response);
  return { data: Array.isArray(data) ? data : [] };
};

/**
 * Get billing summary for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const fetchMyBillingSummary = async (): Promise<IBillingSummary> => {
  const response = await api.get<ApiResponse<IBillingSummary>>('/me/billing/summary');
  return extractData<IBillingSummary>(response);
};

/**
 * Whether the signed-in user's organization is locked out for billing
 * (CVA-4668). Any role may call it, and it stays reachable while locked.
 */
export const fetchSubscriptionLockStatus = async (): Promise<SubscriptionLockStatusResponse> => {
  const response = await api.get<ApiResponse<SubscriptionLockStatusResponse>>(
    '/subscription/lockout-status',
  );
  return extractData<SubscriptionLockStatusResponse>(response);
};

/**
 * Get grace period status for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const fetchMyGracePeriodStatus = async (): Promise<GracePeriodStatus> => {
  const response = await api.get<ApiResponse<GracePeriodStatus>>('/me/grace-period-status');
  return extractData<GracePeriodStatus>(response);
};

/**
 * Get trial status for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 * Derives trial status from the subscription data
 */
export const fetchMyTrialStatus = async (): Promise<TrialStatus> => {
  const response = await api.get<ApiResponse<{ subscription: OrganizationSubscription }>>(
    '/me/subscription',
  );

  const data = extractData<{ subscription: OrganizationSubscription }>(response);
  const subscription = data.subscription;

  if (!subscription.is_trial) {
    return {
      isTrial: false,
      trialDaysRemaining: null,
      trialEndsAt: null,
      severity: null,
    };
  }

  // BE returns camelCase `trialEndDate`; fall back to snake_case for safety
  const rawEndDate = subscription.trialEndDate || subscription.trial_end_date;
  const trialEndsAt = rawEndDate ? new Date(rawEndDate as string) : null;
  if (!trialEndsAt) {
    return {
      isTrial: true,
      trialDaysRemaining: null,
      trialEndsAt: null,
      severity: 'info',
    };
  }

  const daysRemaining = calculateTrialDisplayDaysRemaining(trialEndsAt) ?? 0;

  let severity: 'info' | 'warning' | 'critical' = 'info';
  if (daysRemaining <= 2) {
    severity = 'critical';
  } else if (daysRemaining <= 5) {
    severity = 'warning';
  }

  return {
    isTrial: true,
    trialDaysRemaining: Math.max(0, daysRemaining),
    trialEndsAt: rawEndDate || null,
    severity,
  };
};

/**
 * Get subscription features for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const fetchMySubscriptionFeatures = async (): Promise<{
  data: { features: PlanFeature[]; featuresByCategory: Record<string, PlanFeature[]> };
}> => {
  const response = await api.get<
    ApiResponse<{ features: PlanFeature[]; featuresByCategory: Record<string, PlanFeature[]> }>
  >('/me/features');
  return {
    data: extractData<{
      features: PlanFeature[];
      featuresByCategory: Record<string, PlanFeature[]>;
    }>(response),
  };
};

// ========================================
// SUBSCRIPTION WRITE OPERATIONS (JWT-based /me/ endpoints)
// These functions use the JWT token to identify the organization
// No organizationId parameter needed
// ========================================

/**
 * Update subscription for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const updateMySubscription = async (data: {
  plan_id?: string;
  status?: string;
  auto_renew?: boolean;
}): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.put<ApiResponse<OrganizationSubscription>>('/me/subscription', data);
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Create subscription for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const createMySubscription = async (data: {
  plan_id: string;
  student_count?: number;
  is_trial?: boolean;
  trial_days?: number;
}): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>('/me/subscription', data);
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Cancel subscription for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const cancelMySubscription = async (): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.delete<ApiResponse<OrganizationSubscription>>('/me/subscription');
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Upgrade subscription plan for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const upgradeMyPlan = async (
  newPlanId: string,
  reason?: string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    '/me/subscription/upgrade',
    { newPlanId, reason },
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Schedule downgrade for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const scheduleMyDowngrade = async (
  newPlanId: string,
  reason?: string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    '/me/subscription/downgrade',
    { newPlanId, reason },
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Get scheduled downgrade status for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const fetchMyScheduledDowngradeStatus = async (): Promise<{
  data: {
    hasScheduledDowngrade: boolean;
    scheduledPlan?: SubscriptionPlan;
    effectiveDate?: string;
  };
}> => {
  const response = await api.get<
    ApiResponse<{
      hasScheduledDowngrade: boolean;
      scheduledPlan?: SubscriptionPlan;
      effectiveDate?: string;
    }>
  >('/me/subscription/scheduled-downgrade');
  return { data: extractData(response) };
};

/**
 * Cancel scheduled downgrade for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const cancelMyScheduledDowngrade = async (): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    '/me/subscription/cancel-scheduled-downgrade',
    {},
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Schedule subscription cancellation for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const scheduleMySubscriptionCancellation = async (
  reason?: string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    '/me/subscription/schedule-cancellation',
    { reason },
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Reactivate subscription for current user's organization (uses JWT automatically)
 * Standardized approach - no organization ID parameter needed
 */
export const reactivateMySubscription = async (): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    '/me/subscription/reactivate',
    {},
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Get single billing invoice by ID
 */
export const fetchBillingInvoiceById = async (
  invoiceId: string,
): Promise<{ data: BillingInvoice }> => {
  const response = await api.get<ApiResponse<BillingInvoice>>(
    `/subscription/billing/invoice/${invoiceId}`,
  );
  return { data: extractData<BillingInvoice>(response) };
};

/**
 * Download billing invoice as PDF
 */
export const downloadInvoicePdf = async (invoiceId: string): Promise<Blob> => {
  const response = await api.get(`/subscription/billing/invoice/${invoiceId}/download`, {
    responseType: 'blob',
  });
  return response.data as Blob;
};

/**
 * Get payment history for a subscription
 */
export const fetchPaymentHistory = async (
  subscriptionId: number,
): Promise<{ data: PaymentHistory[] }> => {
  const response = await api.get<ApiResponse<PaymentHistory[]>>(
    `/subscription/payment/history/subscription/${subscriptionId}`,
  );
  return { data: extractData<PaymentHistory[]>(response) };
};

/**
 * Get a single payment record with relations
 */
export const fetchPaymentDetails = async (paymentId: string): Promise<{ data: PaymentHistory }> => {
  const response = await api.get<ApiResponse<PaymentHistory>>(
    `/subscription/payment/history/${paymentId}`,
  );
  return { data: extractData<PaymentHistory>(response) };
};

/**
 * Get invoice payload for a payment (used for download/view)
 */
export const fetchPaymentInvoice = async (paymentId: string): Promise<{ data: InvoicePayload }> => {
  const response = await api.get<ApiResponse<InvoicePayload>>(
    `/subscription/payment/history/${paymentId}/invoice`,
  );
  return { data: extractData<InvoicePayload>(response) };
};

/**
 * Convert trial to paid subscription
 */
export const convertTrialToPaid = async (
  subscriptionId: number | string,
): Promise<{ data: OrganizationSubscription }> => {
  const response = await api.post<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${subscriptionId}/convert-to-paid`,
  );
  return { data: extractData<OrganizationSubscription>(response) };
};

/**
 * Get comprehensive billing summary for dashboard widget
 */
export const getBillingSummary = async (organizationId: string): Promise<IBillingSummary> => {
  const response = await api.get<ApiResponse<IBillingSummary>>(
    `/subscription/billing/organization/${organizationId}/summary`,
  );
  return extractData<IBillingSummary>(response);
};

/**
 * Get grace period status for banner display
 */
export const getGracePeriodStatus = async (organizationId: string): Promise<GracePeriodStatus> => {
  const response = await api.get<ApiResponse<GracePeriodStatus>>(
    `/subscription/organization/${organizationId}/grace-period-status`,
  );
  return extractData<GracePeriodStatus>(response);
};

/**
 * Get trial status for countdown timer
 */
export const getTrialStatus = async (organizationId: string): Promise<TrialStatus> => {
  const response = await api.get<ApiResponse<OrganizationSubscription>>(
    `/subscription/organization/${organizationId}`,
  );

  const data = extractData<{ subscription: OrganizationSubscription }>(response);
  const subscription = data.subscription;

  if (!subscription.is_trial) {
    return {
      isTrial: false,
      trialDaysRemaining: null,
      trialEndsAt: null,
      severity: null,
    };
  }

  const trialEndsAt = subscription.trial_end_date ? new Date(subscription.trial_end_date) : null;
  if (!trialEndsAt) {
    return {
      isTrial: true,
      trialDaysRemaining: null,
      trialEndsAt: null,
      severity: 'info',
    };
  }

  const daysRemaining = calculateTrialDisplayDaysRemaining(trialEndsAt) ?? 0;

  let severity: 'info' | 'warning' | 'critical' = 'info';
  if (daysRemaining <= 2) {
    severity = 'critical';
  } else if (daysRemaining <= 5) {
    severity = 'warning';
  }

  return {
    isTrial: true,
    trialDaysRemaining: Math.max(0, daysRemaining),
    trialEndsAt: subscription.trial_end_date || null,
    severity,
  };
};

// ========================================
// SUBSCRIPTION AGREEMENT
// ========================================

export interface ISubscriptionAgreementSignRequest {
  signId: string;
  overrides?: {
    organization?: { name?: string; registration_no?: string; address?: string };
    user?: { first_name?: string; last_name?: string; title?: string };
  };
}

/**
 * Fetch agreement signed status for the current user's organization
 */
export const getSubscriptionAgreementStatus = async (): Promise<{ signed: boolean }> => {
  const response = await api.get<ApiResponse<{ signed: boolean }>>(
    '/subscription/agreement/status',
  );
  return extractData<{ signed: boolean }>(response);
};

/**
 * Sign the subscription agreement (one-time, authenticated users only)
 */
export const signSubscriptionAgreement = async (
  data: ISubscriptionAgreementSignRequest,
): Promise<{ signed: boolean }> => {
  const response = await api.post<ApiResponse<{ signed: boolean }>>(
    '/subscription/agreement/sign',
    data,
  );
  return extractData<{ signed: boolean }>(response);
};

export default {
  fetchSubscriptionPlans,
  fetchPlanDetails,
  fetchCurrentSubscription,
  fetchMySubscriptionWithFeatures,
  fetchOrganizationSubscription,
  createSubscription,
  updateSubscription,
  cancelSubscription,
  scheduleCancellation,
  fetchSubscriptionFeatures,
  checkFeature,
  initiatePayment,
  fetchMyBillingHistory,
  fetchMyBillingSummary,
  fetchMyGracePeriodStatus,
  fetchMyTrialStatus,
  fetchMySubscriptionFeatures,
  // New /me/ write functions (JWT-based, no organizationId needed)
  createMySubscription,
  updateMySubscription,
  cancelMySubscription,
  upgradeMyPlan,
  scheduleMyDowngrade,
  fetchMyScheduledDowngradeStatus,
  cancelMyScheduledDowngrade,
  scheduleMySubscriptionCancellation,
  reactivateMySubscription,
  fetchBillingInvoiceById,
  downloadInvoicePdf,
  fetchPaymentHistory,
  fetchPaymentDetails,
  fetchPaymentInvoice,
  convertTrialToPaid,
  getBillingSummary,
  getGracePeriodStatus,
  getTrialStatus,
  getSubscriptionAgreementStatus,
  signSubscriptionAgreement,
};
